const L2_BUCKET = "unidata-nexrad-level2";
const L3_BUCKET = "unidata-nexrad-level3";
// Real-time Level 2: each in-progress volume is streamed as numbered chunks
// under STATION/<volume 1-999>/, minutes before the archive bucket posts it.
const L2_CHUNKS_BUCKET = "unidata-nexrad-level2-chunks";
const S3_HOST = (bucket) => `https://${bucket}.s3.amazonaws.com`;

function proxyUrl(bucket, { key, query }) {
  const params = new URLSearchParams({ bucket });
  if (key) params.set("key", key);
  if (query) params.set("qs", query);
  return `/s3-proxy?${params.toString()}`;
}

const listCache = new Map();

function fetchInit(signal, headers) {
  const init = {};
  if (signal) init.signal = signal;
  if (headers && Object.keys(headers).length) init.headers = headers;
  return init;
}

function fetchOk(resp) {
  return resp.ok || resp.status === 206 || resp.status === 404;
}

async function fetchWithCorsFallback(directUrl, bucket, spec, signal, headers) {
  const init = fetchInit(signal, headers);
  const ranged = Boolean(headers?.Range);
  try {
    const resp = await fetch(directUrl, init);
    if (fetchOk(resp)) return resp;
    if (resp.status === 403 || resp.status === 0) {
      return fetch(proxyUrl(bucket, spec), init);
    }
    return resp;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    if (ranged) throw err;
    return fetch(proxyUrl(bucket, spec), fetchInit(signal));
  }
}

function parseContentRange(header) {
  const match = String(header || "").match(/bytes\s+(\d+)-(\d+)\/(\d+|\*)/i);
  if (!match) return null;
  return {
    start: Number(match[1]),
    end: Number(match[2]),
    total: match[3] === "*" ? 0 : Number(match[3]),
  };
}

export function clearS3ListCache() {
  listCache.clear();
}

function xmlTag(xmlText, tag) {
  const match = xmlText.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
  return match ? match[1] : "";
}

function parseListXml(xmlText) {
  const contents = [];
  const re = /<Contents>([\s\S]*?)<\/Contents>/g;
  let match;
  while ((match = re.exec(xmlText))) {
    const block = match[1];
    contents.push({
      key: xmlTag(block, "Key"),
      size: parseInt(xmlTag(block, "Size") || "0", 10),
    });
  }
  return {
    contents,
    prefixes: [...xmlText.matchAll(/<CommonPrefixes>\s*<Prefix>([^<]*)<\/Prefix>/g)].map((m) => m[1]),
    truncated: xmlTag(xmlText, "IsTruncated") === "true",
    token: xmlTag(xmlText, "NextContinuationToken"),
  };
}

async function listS3PrefixUncached(bucket, prefix, { signal, delimiter = "", maxKeys = 0 } = {}) {
  const objects = [];
  const prefixes = [];
  let token = "";
  do {
    const params = new URLSearchParams({
      "list-type": "2",
      prefix,
      "max-keys": String(maxKeys || 1000),
    });
    if (delimiter) params.set("delimiter", delimiter);
    if (token) params.set("continuation-token", token);
    const query = params.toString();
    const directUrl = `${S3_HOST(bucket)}/?${query}`;
    const resp = await fetchWithCorsFallback(directUrl, bucket, { query }, signal);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (!resp.ok) {
      throw new Error(`S3 list failed (${resp.status}) for ${prefix}`);
    }
    const xml = await resp.text();
    const page = parseListXml(xml);
    objects.push(...page.contents);
    prefixes.push(...page.prefixes);
    token = page.truncated && !maxKeys ? page.token : "";
  } while (token);
  return delimiter ? { objects, prefixes } : objects;
}

/** Uncached listing for buckets that change under us (real-time chunks). */
export function listS3Fresh(bucket, prefix, { signal, maxKeys = 0 } = {}) {
  return listS3PrefixUncached(bucket, prefix, { signal, maxKeys });
}

/** Immediate "subdirectories" of prefix (S3 CommonPrefixes), uncached. */
export async function listS3Dirs(bucket, prefix, { signal } = {}) {
  const { prefixes } = await listS3PrefixUncached(bucket, prefix, { signal, delimiter: "/" });
  return prefixes;
}

export async function listS3Prefix(bucket, prefix, { signal, skipCache = false } = {}) {
  const cacheKey = `${bucket}:${prefix}`;
  if (!skipCache && listCache.has(cacheKey)) return listCache.get(cacheKey);
  const pending = listS3PrefixUncached(bucket, prefix, { signal });
  if (!skipCache) listCache.set(cacheKey, pending);
  try {
    return await pending;
  } catch (err) {
    if (!skipCache) listCache.delete(cacheKey);
    throw err;
  }
}

export async function getS3Object(bucket, key, { signal, onProgress, start = 0, onChunk } = {}) {
  const directUrl = `${S3_HOST(bucket)}/${key.split("/").map(encodeURIComponent).join("/")}`;
  const headers = start > 0 ? { Range: `bytes=${start}-` } : undefined;
  const resp = await fetchWithCorsFallback(directUrl, bucket, { key }, signal, headers);
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (start > 0 && resp.status === 200) {
    throw new Error(`S3 range not supported for ${key}`);
  }
  if (!resp.ok && resp.status !== 206) {
    throw new Error(`S3 get failed (${resp.status}) for ${key}`);
  }
  const contentLength = parseInt(resp.headers.get("content-length") || "0", 10);
  const range = parseContentRange(resp.headers.get("content-range"));
  const objectSize = range?.total || (start > 0 ? 0 : contentLength);
  const total = objectSize || start + contentLength;
  const streaming = Boolean(resp.body) && Boolean(onProgress || onChunk);
  if (!streaming) {
    return new Uint8Array(await resp.arrayBuffer());
  }
  const reader = resp.body.getReader();
  const chunks = [];
  let loaded = 0;
  let stopped = false;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;
      onProgress?.({ loaded: start + loaded, total: total || start + loaded, key });
      if (onChunk?.(value)) {
        stopped = true;
        await reader.cancel().catch(() => {});
        break;
      }
    }
  } catch (err) {
    if (!(stopped && err?.name === "AbortError")) throw err;
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

export function listL2Prefix(station, dateClean) {
  const year = dateClean.slice(0, 4);
  const month = dateClean.slice(4, 6);
  const day = dateClean.slice(6, 8);
  return `${year}/${month}/${day}/${station}/`;
}

export function listL3Prefix(station, dateClean, productCode) {
  const site = level3StationId(station);
  const y = dateClean.slice(0, 4);
  const m = dateClean.slice(4, 6);
  const d = dateClean.slice(6, 8);
  return `${site}_${productCode}_${y}_${m}_${d}_`;
}

export function level3StationId(station) {
  const id = String(station || "").toUpperCase();
  if (id.length === 4 && "KPT".includes(id[0])) return id.slice(1);
  return id;
}

export { L2_BUCKET, L2_CHUNKS_BUCKET, L3_BUCKET };
